import CustomFilterType from "../models/CustomFilterType";
import Organization from "../models/serviceModels/Organization";
import { getEthnicities, getGenders, getRaces, getRelationships } from "../services/clientsService";
import { searchPrograms } from "../services/programsService";
import * as filterDataTypes from "./customFilterDataType";
import { FORMS, HOUSEHOLD_MEMBER, PARTICIPANT, PROGRAMS } from "./filterGrouping";
import { DEMOGRAPHICS, SUBMITTED_FORMS } from "./reportTypes";
import states from "./states";
import { searchUsers } from "../services/userService";
import { assignedUserEnabled, participantWorkedWithYearFilterEnabled } from "../utils/features";
import {
    getEthnicityFieldVisible,
    getGenderOptionsForOrganization,
    getRaceFieldLabel,
    getRaceOptionsForOrganization,
    isDemographicVisible
} from "../utils/demographicsSchema";

export const PARTICIPANT_BIRTH_DATE = 'PARTICIPENT_BIRTH_DATE';
export const PARTICIPANT_GENDER = 'PARTICIPANT_GENDER';
export const PARTICIPANT_ETHNICITY = 'PARTICIPANT_ETHNICITY';
export const PARTICIPANT_RACE = 'PARTICIPANT_RACE';
export const PARTICIPANT_CITY = 'PARTICIPANT_CITY';
export const PARTICIPANT_STATE = 'PARTICIPANT_STATE';
export const PARTICIPANT_AGE = 'PARTICIPANT_AGE';
export const PARTICIPANT_ZIP = 'PARTICIPANT_ZIP';
export const PARTICIPANT_CREATED = 'PARTICIPANT_CREATED';
export const PARTICIPANT_UPDATED = 'PARTICIPANT_UPDATED';
export const PARTICIPANT_ACTIVATED = 'PARTICIPANT_ACTIVATED';
export const PARTICIPANT_IS_ACTIVATED = 'PARTICIPANT_IS_ACTIVATED';
export const PARTICIPANT_DEACTIVATED = 'PARTICIPANT_DEACTIVATED';
export const PARTICIPANT_IS_DEACTIVATED = 'PARTICIPANT_IS_DEACTIVATED';
export const PARTICIPANT_ADDITIONAL_INFORMATION = 'PARTICIPANT_ADDITIONAL_INFORMATION';
export const PARTICIPANT_ASSIGNED_TO = 'PARTICIPANT_ASSIGNED_TO';
export const PARTICIPANT_WORKED_WITH_YEAR = 'PARTICIPANT_WORKED_WITH_YEAR';
export const HOUSEHOLD_MEMBER_AGE = 'HOUSEHOLD_MEMBER_AGE';
export const HOUSEHOLD_MEMBER_BIRTH_DATE = 'HOUSEHOLD_MEMBER_BIRTH_DATE';
export const HOUSEHOLD_MEMBER_GENDER = 'HOUSEHOLD_MEMBER_GENDER';
export const HOUSEHOLD_MEMBER_ETHNICITY = 'HOUSEHOLD_MEMBER_ETHNICITY';
export const HOUSEHOLD_MEMBER_RACE = 'HOUSEHOLD_MEMBER_RACE';
export const HOUSEHOLD_MEMBER_RELATIONSHIP = 'HOUSEHOLD_MEMBER_RELATIONSHIP';
export const PROGRAM_ENROLLED_IN = 'PROGRAM_ENROLLED_IN';
export const PROGRAM_ENROLLED_DATE = 'PROGRAM_ENROLLED_DATE';
export const PROGRAM_UNENROLLED_DATE = 'PROGRAM_UNENROLLED_DATE';
export const PROGRAM_ENROLLED_DURING_DATE = 'PROGRAM_ENROLLED_DURING_DATE';
export const FORM_ACTIVITY_DATE = 'FORM_ACTIVITY_DATE';
export const FORM_FIELD = 'FORM_FIELD';
export const RIGHT_PARENTHESIS = 'RIGHT_PARENTHESIS';
export const LEFT_PARENTHESIS = 'LEFT_PARENTHESIS';
export const AND = 'AND';
export const OR = 'OR';

const sourceFilters: CustomFilterType[] = [
    {
        type: PARTICIPANT_BIRTH_DATE,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Birth Date'
    },
    {
        type: PARTICIPANT_GENDER,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getGenders().then(g => {
            return g.map(g1 => {
                return {
                    label: g1.name,
                    value: g1.id.toString()
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Gender'
    },
    {
        type: PARTICIPANT_ETHNICITY,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getEthnicities().then(e => {
            return e.map(e1 => {
                return {
                    value: e1.id.toString(),
                    label: e1.name
                }
            });
        }),
        isExtenalLookup: false,
        name: 'Ethnicity'
    },
    {
        type: PARTICIPANT_RACE,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getRaces().then(r => {
            return r.map(r1 => {
                return {
                    value: r1.id.toString(),
                    label: r1.name
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Race'
    },
    {
        type: PARTICIPANT_CITY,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING,
        isExtenalLookup: false,
        name: 'Address City'
    },
    {
        type: PARTICIPANT_STATE,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => Promise.resolve(states),
        isExtenalLookup: false,
        name: 'Address State'
    },
    {
        type: PARTICIPANT_ZIP,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING,
        isExtenalLookup: false,
        name: 'Address Zip'
    },
    {
        type: PARTICIPANT_CREATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Created Date'
    },
    {
        type: PARTICIPANT_UPDATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Updated Date'
    },
    {
        type: PARTICIPANT_ACTIVATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Activated Date'
    },
    {
        type: PARTICIPANT_IS_ACTIVATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.BOOLEAN,
        isExtenalLookup: false,
        name: 'Is Activated'
    },
    {
        type: PARTICIPANT_DEACTIVATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Deactivated Date'
    },
    {
        type: PARTICIPANT_IS_DEACTIVATED,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.BOOLEAN,
        isExtenalLookup: false,
        name: 'Is Deactivated'
    },
    {
        type: PARTICIPANT_ADDITIONAL_INFORMATION,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: null,
        isExtenalLookup: true,
        name: 'Additional Information'
    },
    {
        type: PARTICIPANT_ASSIGNED_TO,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => searchUsers("", 0, 1000).then(r => {
            return r.data.map(u => {
                return {
                    label: u.firstName + ' ' + u.lastName,
                    value: u.id.toString(),
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Assigned To'
    },
    {
        type: PARTICIPANT_WORKED_WITH_YEAR,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Worked With Date'
    },
    {
        type: HOUSEHOLD_MEMBER_BIRTH_DATE,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.DATE,
        isExtenalLookup: false,
        name: 'Birth Date'
    },
    {
        type: HOUSEHOLD_MEMBER_GENDER,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getGenders().then(g => {
            return g.map(g1 => {
                return {
                    label: g1.name,
                    value: g1.id.toString()
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Gender'
    },
    {
        type: HOUSEHOLD_MEMBER_ETHNICITY,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getEthnicities().then(e => {
            return e.map(e1 => {
                return {
                    value: e1.id.toString(),
                    label: e1.name
                }
            });
        }),
        isExtenalLookup: false,
        name: 'Ethnicity'
    },
    {
        type: HOUSEHOLD_MEMBER_RACE,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getRaces().then(r => {
            return r.map(r1 => {
                return {
                    value: r1.id.toString(),
                    label: r1.name
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Race'
    },
    {
        type: PARTICIPANT_AGE,
        grouping: PARTICIPANT,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: null,
        isExtenalLookup: false,
        name: 'Age'
    },
    {
        type: HOUSEHOLD_MEMBER_RELATIONSHIP,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => getRelationships().then(r => {
            return r.map(r1 => {
                return {
                    value: r1.id.toString(),
                    label: r1.name
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Relationsip to Participant'
    },
    {
        type: HOUSEHOLD_MEMBER_AGE,
        grouping: HOUSEHOLD_MEMBER,
        reportTypes: [DEMOGRAPHICS, SUBMITTED_FORMS],
        dataType: null,
        isExtenalLookup: false,
        name: 'Age'
    },
    {
        type: PROGRAM_ENROLLED_IN,
        grouping: PROGRAMS,
        reportTypes: [DEMOGRAPHICS],
        dataType: filterDataTypes.STRING_WITH_POSSIBLE_VALUES,
        possibleValues: () => searchPrograms({ take: 1000 }).then(r => {
            return r.data.map(r1 => {
                return {
                    value: r1.id,
                    label: r1.name
                }
            })
        }),
        isExtenalLookup: false,
        name: 'Currently Enrolled In'
    },

    {
        type: PROGRAM_ENROLLED_DATE,
        grouping: PROGRAMS,
        reportTypes: [DEMOGRAPHICS],
        dataType: null,
        isExtenalLookup: false,
        name: 'Enrollment Started'
    },
    {
        type: PROGRAM_UNENROLLED_DATE,
        grouping: PROGRAMS,
        reportTypes: [DEMOGRAPHICS],
        dataType: null,
        isExtenalLookup: false,
        name: 'Enrollment Ended'
    },
    {
        type: PROGRAM_ENROLLED_DURING_DATE,
        grouping: PROGRAMS,
        reportTypes: [DEMOGRAPHICS],
        dataType: null,
        isExtenalLookup: false,
        name: 'Enrolled During'
    },
    {
        type: FORM_FIELD,
        grouping: FORMS,
        reportTypes: [SUBMITTED_FORMS],
        dataType: null,
        isExtenalLookup: true,
        name: 'Form Field'
    }
];

const getBaseFilters = () => assignedUserEnabled ?
    sourceFilters :
    sourceFilters.filter(sf => sf.type !== PARTICIPANT_ASSIGNED_TO);

const getFeatureFlaggedFilters = () => participantWorkedWithYearFilterEnabled ?
    getBaseFilters() :
    getBaseFilters().filter(sf => sf.type !== PARTICIPANT_WORKED_WITH_YEAR);

const isGenderFilter = (type: string) => {
    return type === PARTICIPANT_GENDER || type === HOUSEHOLD_MEMBER_GENDER;
};

const isEthnicityFilter = (type: string) => {
    return type === PARTICIPANT_ETHNICITY || type === HOUSEHOLD_MEMBER_ETHNICITY;
};

const isRaceFilter = (type: string) => {
    return type === PARTICIPANT_RACE || type === HOUSEHOLD_MEMBER_RACE;
};

const isFilterVisibleForOrganization = (filter: CustomFilterType, organization?: Organization) => {
    if (isGenderFilter(filter.type)) {
        return isDemographicVisible(organization, 'gender');
    }

    if (isEthnicityFilter(filter.type)) {
        return getEthnicityFieldVisible(organization);
    }

    if (isRaceFilter(filter.type)) {
        return isDemographicVisible(organization, 'race');
    }

    return true;
};

const getFilterNameForOrganization = (filter: CustomFilterType, organization?: Organization) => {
    if (isRaceFilter(filter.type)) {
        return getRaceFieldLabel(organization);
    }

    return filter.name;
};

const getPossibleValuesForOrganization = (filter: CustomFilterType, organization?: Organization) => {
    if (isGenderFilter(filter.type)) {
        return () => getGenders().then(genders => getGenderOptionsForOrganization(genders, organization));
    }

    if (isEthnicityFilter(filter.type)) {
        return () => getEthnicities().then(ethnicities => {
            return ethnicities.map(ethnicity => ({
                value: ethnicity.id.toString(),
                label: ethnicity.name
            }));
        });
    }

    if (isRaceFilter(filter.type)) {
        return () => Promise.all([getRaces(), getEthnicities()]).then(([races, ethnicities]) => {
            return getRaceOptionsForOrganization(races, ethnicities, organization);
        });
    }

    return filter.possibleValues;
};

const getAvailableCustomFilters = (organization?: Organization): CustomFilterType[] => {
    return getFeatureFlaggedFilters()
        .filter(filter => isFilterVisibleForOrganization(filter, organization))
        .map(filter => ({
            ...filter,
            name: getFilterNameForOrganization(filter, organization),
            possibleValues: getPossibleValuesForOrganization(filter, organization)
        }));
};

export const filters = getAvailableCustomFilters();

export {
    getAvailableCustomFilters
};
